import React from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    }

    // In production, you would send this to an error tracking service
    // e.g., Sentry, LogRocket, etc.
    // this.logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <div className="bg-gray-800 border border-red-500 rounded-lg p-8 shadow-2xl">
              {/* Error Icon */}
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-12 h-12 text-white" />
                </div>
              </div>

              {/* Error Title */}
              <h1 className="text-3xl font-bold text-center mb-4 text-red-400">
                Oops! Something went wrong
              </h1>

              {/* Error Message */}
              <p className="text-gray-300 text-center mb-6">
                We encountered an unexpected error. Don't worry, your data is safe.
              </p>

              {/* Error Count Warning */}
              {this.state.errorCount > 2 && (
                <div className="bg-yellow-900 border border-yellow-600 rounded-lg p-4 mb-6">
                  <p className="text-yellow-200 text-sm">
                    <strong>Warning:</strong> Multiple errors detected ({this.state.errorCount}).
                    Consider reloading the page or contacting support if the issue persists.
                  </p>
                </div>
              )}

              {/* Development Error Details */}
              {isDevelopment && this.state.error && (
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 mb-6 max-h-64 overflow-auto">
                  <p className="text-red-400 font-semibold mb-2">Error Details (Development Only):</p>
                  <p className="text-sm text-gray-300 mb-2">
                    <strong>Message:</strong> {this.state.error.message}
                  </p>
                  <details className="text-xs text-gray-400">
                    <summary className="cursor-pointer mb-2 hover:text-white">
                      Stack Trace (click to expand)
                    </summary>
                    <pre className="whitespace-pre-wrap break-words bg-gray-950 p-3 rounded mt-2">
                      {this.state.error.stack}
                    </pre>
                  </details>
                  {this.state.errorInfo && (
                    <details className="text-xs text-gray-400 mt-3">
                      <summary className="cursor-pointer mb-2 hover:text-white">
                        Component Stack (click to expand)
                      </summary>
                      <pre className="whitespace-pre-wrap break-words bg-gray-950 p-3 rounded mt-2">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  <RefreshCw className="w-5 h-5" />
                  Try Again
                </button>

                <button
                  onClick={this.handleReload}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  <RefreshCw className="w-5 h-5" />
                  Reload Page
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                  <Home className="w-5 h-5" />
                  Go Home
                </button>
              </div>

              {/* Support Message */}
              <div className="mt-6 pt-6 border-t border-gray-700 text-center">
                <p className="text-gray-400 text-sm">
                  If this problem continues, please contact support with the error details above.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
